import { BusinessScenario } from '@/types/scenarios/business';
import { Rocket, Users, TrendingUp, Zap, Target, HeartHandshake } from 'lucide-react';

export const saasStartup: BusinessScenario = {
  meta: {
    id: 'saas-startup',
    slug: 'saas-startup',
    name: 'SaaS Startup',
    tagline: 'Scale your SaaS business with AI-powered customer engagement',
    description: 'Built for fast-growing SaaS companies that need to provide exceptional customer experiences while maintaining lean operations.',
    size: 'startup',
    employeeCount: '5-25',
    annualRevenue: '$100K - $2M ARR',
    challenges: [
      'Limited resources for 24/7 customer support',
      'High customer acquisition costs',
      'Need to reduce churn and improve retention',
      'Complex onboarding processes',
      'Scaling support without scaling costs',
      'Converting free trials to paid customers'
    ],
    goals: [
      'Automate customer onboarding and activation',
      'Provide instant technical support',
      'Increase trial-to-paid conversion rates',
      'Reduce customer churn',
      'Scale customer success operations',
      'Gather product feedback efficiently'
    ],
    characteristics: [
      'Fast-moving and agile',
      'Product-led growth focus',
      'Data-driven decision making',
      'Remote-first teams'
    ],
    isFeatured: true
  },
  
  hero: {
    headline: 'Accelerate Your SaaS Growth',
    subheadline: 'Deploy AI voice agents to scale customer success without scaling costs',
  },
  
  recommendedPlaybooks: [
    'first-login-welcome',
    'feature-explainer',
    'demo-scheduler',
    'interactive-faq',
    'customer-check-in',
    'proactive-problem-solver'
  ],
  
  useCases: {
    primary: [
      {
        id: 'user-onboarding',
        title: 'Automated User Onboarding',
        description: 'Guide new users through setup and initial value realization',
        icon: Rocket,
        playbookIds: ['first-login-welcome', 'feature-explainer'],
        priority: 'high',
        expectedOutcome: 'Reduce time-to-value by 60%'
      },
      {
        id: 'trial-conversion',
        title: 'Trial-to-Paid Conversion',
        description: 'Engage trial users at critical moments to drive conversions',
        icon: Target,
        playbookIds: ['demo-scheduler', 'customer-check-in'],
        priority: 'high',
        expectedOutcome: 'Increase conversion rate by 35%'
      },
      {
        id: 'technical-support',
        title: '24/7 Technical Support',
        description: 'Provide instant answers to technical questions and troubleshooting',
        icon: Zap,
        playbookIds: ['interactive-faq', 'proactive-problem-solver'],
        priority: 'high',
        expectedOutcome: 'Reduce support tickets by 50%'
      }
    ],
    secondary: [
      {
        id: 'customer-success',
        title: 'Proactive Customer Success',
        description: 'Check in with customers and prevent churn before it happens',
        icon: HeartHandshake,
        playbookIds: ['customer-check-in'],
        priority: 'medium',
        expectedOutcome: 'Reduce churn by 25%'
      }
    ]
  },
  
  benefits: [
    {
      title: 'Scale Without Hiring',
      description: 'Handle 10x more customer interactions without adding headcount',
      metric: '90% cost reduction vs human agents',
      icon: TrendingUp
    },
    {
      title: 'Improve Activation Rates',
      description: 'Get users to their "aha moment" faster with guided onboarding',
      metric: '60% faster time-to-value',
      icon: Rocket
    },
    {
      title: 'Reduce Churn',
      description: 'Proactively engage at-risk customers before they leave',
      metric: '25% reduction in churn',
      icon: Users
    },
    {
      title: 'Increase Conversions',
      description: 'Convert more trials with personalized engagement',
      metric: '35% higher conversion rates',
      icon: Target
    }
  ],
  
  metrics: {
    averageROI: '450%',
    implementationTime: '1-2 days',
    supportTicketReduction: '55%',
    customerSatisfaction: '4.8/5',
    costSavings: '$5,000/month'
  },
  
  integrations: {
    essential: [
      'CRM (HubSpot, Salesforce)',
      'Help desk (Intercom, Zendesk)',
      'Analytics (Mixpanel, Amplitude)'
    ],
    recommended: [
      'Slack notifications',
      'Calendar scheduling (Calendly)',
      'Payment systems (Stripe)',
      'Email automation'
    ]
  },
  
  demoConfig: {
    firstMessage: "Hey there! Welcome to [Your SaaS]. I'm here to help you get the most out of our platform. Whether you need help getting started, have technical questions, or want to explore advanced features, I've got you covered!",
    systemPrompt: "You are a knowledgeable customer success agent for a SaaS startup. Be helpful, technical when needed, and focused on helping users achieve their goals quickly. Always aim to reduce friction and accelerate value realization.",
    exampleQuestions: [
      "How do I integrate with my existing tools?",
      "What's included in the free trial?",
      "Can you walk me through the API?",
      "How do I upgrade my plan?"
    ]
  },
  
  successStories: [
    {
      companyName: 'CloudFlow Analytics',
      companySize: '12 employees',
      industry: 'B2B SaaS',
      challenge: 'Struggling with high trial abandonment rates and overwhelming support tickets from new users',
      solution: 'Deployed Help Genie for automated onboarding, proactive user assistance, and 24/7 technical support',
      results: [
        'Trial-to-paid conversion increased from 14% to 31%',
        'Support ticket volume reduced by 62%',
        'Customer activation time reduced from 7 days to 2 days',
        'Saved $6,000/month on customer success costs'
      ],
      quote: {
        text: "Help Genie allowed us to provide enterprise-level customer success with a startup budget. Our conversion rates have more than doubled, and our small team can now focus on product development.",
        author: 'Alex Chen',
        title: 'Founder & CEO'
      }
    }
  ],
  
  resources: [
    {
      type: 'guide',
      title: 'SaaS Customer Success Playbook',
      description: 'Complete guide to scaling customer success with AI',
      url: '/resources/saas-success-playbook'
    },
    {
      type: 'template',
      title: 'Onboarding Conversation Flows',
      description: 'Pre-built flows for SaaS user onboarding',
      url: '/templates/saas-onboarding',
      downloadable: true
    },
    {
      type: 'webinar',
      title: 'Scaling SaaS Support on a Budget',
      description: 'Learn how successful startups scale support efficiently',
      url: '/webinars/saas-support',
      duration: '45 minutes'
    }
  ],
  
  faqs: [
    {
      question: 'How does this integrate with our existing tech stack?',
      answer: 'Help Genie integrates seamlessly with popular SaaS tools like HubSpot, Intercom, Slack, and more. We offer REST APIs and webhooks for custom integrations.'
    },
    {
      question: 'Can it handle technical product questions?',
      answer: 'Absolutely! You can train your Help Genie on your documentation, API specs, and common technical issues. It learns from every interaction to improve over time.'
    },
    {
      question: 'How do we measure ROI?',
      answer: 'Track metrics like support ticket reduction, conversion rate improvements, time-to-resolution, and customer satisfaction scores. Most SaaS companies see positive ROI within 30 days.'
    },
    {
      question: 'What about data security and compliance?',
      answer: 'Help Genie is SOC 2 compliant, offers GDPR compliance features, and provides enterprise-grade security with encryption at rest and in transit.'
    }
  ]
};